import {useRef, useEffect} from 'react'
import {usePlaces} from '../../../../hooks'
import {defaultAddress} from '../../../../helpers'

import TextField from '@mui/material/TextField'

import '../../../../styles/google/autocomplete.css'


/**
 * A Google Places autocomplete field for Formik forms.
 *
 * Automatically populates address, location, coordinates, and service fields on selection.
 */
const GooglePlacesField = ({
  field: { name, value, onBlur, onChange },
  form:  { touched, errors, setFieldValue, isSubmitting },
  disabled,
  helperText,
  ...props
}) => {
  const inputRef = useRef(null)

  const isError = touched[name] && Boolean(errors[name])

  // Hook that returns the selected place from Google Autocomplete
  const [place] = usePlaces(inputRef.current)


  useEffect(() => {
    if (!place) return

    const {
      name: locationName,
      place_id: serviceId,
      geometry: {location},
      address_components
    } = place

    // Build structured address object
    const address = address_components.reduce((acc, comp) => {
      const { long_name, types } = comp
      
      if (types.includes('street_number')) acc.number = long_name
      else if (types.includes('route')) acc.street = long_name
      else if (types.includes('sublocality')) acc.neighborhood = long_name
      else if (types.includes('locality')) acc.city = long_name
      else if (types.includes('administrative_area_level_1')) acc.state = long_name
      else if (types.includes('country')) acc.country = long_name
      else if (types.includes('postal_code')) acc.postal = long_name
      return acc
    }, { ...defaultAddress })

    // Populate form fields
    setFieldValue('address', address)
    setFieldValue('location', locationName)
    setFieldValue('coordinates', `${location.lng()} ${location.lat()}`)
    setFieldValue('location_service', 'google')
    setFieldValue('location_service_id', serviceId)

  }, [place, setFieldValue])


  return (
    <TextField
      name={name}
      label='Location'
      fullWidth
      value={value}
      error={isError}
      helperText={isError ? errors[name] : helperText}
      onBlur={onBlur}
      onChange={onChange}
      inputRef={inputRef}
      disabled={disabled || isSubmitting}
      {...props}
    />
  )
}

export default GooglePlacesField

